/**
 * Skill-Leiste unten rechts (Masterprompt Abschnitte 81, 82).
 *
 * Bedienung:
 * - **Handy:** Schaltflaeche halten und ziehen - sie wird dabei zum Zielstick:
 *   Die Auslenkung bestimmt Richtung UND Entfernung. Loslassen wirkt, zurueck
 *   zur Schaltflaeche bricht ab. Ein kurzer Tipp nimmt den Skill in die Hand,
 *   der naechste Tipp auf die Spielflaeche loest ihn dort aus.
 * - **Desktop:** Halten und ziehen geht genauso. Zusaetzlich (Produkt-
 *   entscheidung vom 24.09.2026): Ein kurzer Klick nimmt den Skill in die
 *   Hand - der naechste Linksklick auf die Spielflaeche loest ihn dort aus.
 *   Dasselbe machen die Tasten 1, 2, 3 und R.
 *
 * Die Leiste zeigt nur an, was der Server meldet. Ob ein Skill wirklich
 * zustande kommt, entscheidet ausschliesslich der Server.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AIM_STICK_RADIUS } from '../input/SkillInput.js';
import { needsAim } from '../game/skillPreview.js';
import {
  getHero,
  getSkillInSlot,
  getSkillsForHero,
  type SkillDefinition,
} from '@dueline/game-data';
import { isUltimateUnlocked } from '@dueline/game-rules';
import { t } from '../i18n/index.js';
import type { SkillInput } from '../input/SkillInput.js';
import { skillBeschriftung, type Steuerung } from '../input/mausSteuerung.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import { faehigkeitBild } from './faehigkeitBilder.js';

/*
 * HIER STAND SLOT_LABELS (bis zum 25.10.2026).
 *
 * Die Beschriftung war fest eingetragen: 1, 2, 3, R. Seit es die
 * Maussteuerung mit Q/W/E/R gibt, haette sie gelogen - und zwar in genau
 * der Steuerung, in der man die Tasten am meisten braucht. Jetzt leitet sie
 * sich aus der Belegung ab (skillBeschriftung in mausSteuerung.ts).
 */

interface Props {
  snapshot: MatchSnapshot;
  skillInput: SkillInput;
  /**
   * Loest den Skill aus.
   * Ein Bildschirmpunkt von null bedeutet: in Blickrichtung wirken.
   */
  onCast: (
    slot: number,
    screenX: number | null,
    screenY: number | null,
    /** Auslenkung des Zielsticks, falls mit dem Finger gezielt wurde. */
    stick?: { deltaX: number; deltaY: number },
  ) => void;
  /** Die gewaehlte Steuerung - sie bestimmt die Tastenbeschriftung (25.10.2026). */
  steuerung: Steuerung;
}

export function SkillBar({ snapshot, skillInput, onCast, steuerung }: Props) {
  const [aimingSlot, setAimingSlot] = useState<number | null>(null);
  /** Sichtbarer Zielstick: Mittelpunkt und Auslenkung des Knubbels. */
  const [stick, setStick] = useState<{
    originX: number;
    originY: number;
    knobX: number;
    knobY: number;
  } | null>(null);
  const pointerId = useRef<number | null>(null);
  /** Wird gerade mit dem Finger gezielt? Dann gilt die Auslenkung. */
  const stickMode = useRef(false);
  const stickOrigin = useRef({ x: 0, y: 0 });
  /** Meldet die Zuhoerer am Fenster wieder ab. */
  const abbruch = useRef<AbortController | null>(null);

  useEffect(() => {
    // Hervorgehoben wird beides: der gehaltene und der bereitliegende Skill.
    return skillInput.subscribe((state) =>
      setAimingSlot(state?.slot ?? skillInput.getArmed()?.slot ?? null),
    );
  }, [skillInput]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>, slot: number) => {
      if (pointerId.current !== null) return;

      /*
       * Skills, die nur am Hero selbst passieren, kommen sofort beim Druecken
       * (Rueckmeldung vom 25.09.2026). Kein Zielstick, keine Vorschau - es
       * gibt nichts auszurichten.
       */
      const definition = getSkillInSlot(snapshot.heroId, slot);
      if (definition && !needsAim(definition)) {
        skillInput.disarm();
        onCast(slot, null, null);
        return;
      }

      pointerId.current = event.pointerId;
      event.currentTarget.setPointerCapture(event.pointerId);

      /*
       * Mit dem Finger wird gestickt, mit der Maus direkt gezielt. Der
       * Mittelpunkt ist die Mitte der Schaltflaeche - nicht der Punkt, an dem
       * der Finger aufgesetzt hat. So liegt der Stick immer gleich.
       */
      const istFinger = event.pointerType !== 'mouse';
      stickMode.current = istFinger;
      const flaeche = event.currentTarget.getBoundingClientRect();
      stickOrigin.current = {
        x: flaeche.left + flaeche.width / 2,
        y: flaeche.top + flaeche.height / 2,
      };
      if (istFinger) {
        setStick({ ...toStick(stickOrigin.current, event.clientX, event.clientY) });
      }

      skillInput.begin(slot, event.clientX, event.clientY, istFinger);

      /*
       * Sicherheitsnetz am Fenster: Geht das Loslassen verloren - etwa weil
       * die Schaltflaeche waehrenddessen ausgegraut oder ausgetauscht wird -,
       * bliebe der Zielvorgang sonst fuer immer offen. Dann klebte die
       * Zielvorschau im Bild und kein weiterer Skill liesse sich mehr wirken.
       */
      abbruch.current?.abort();
      const controller = new AbortController();
      abbruch.current = controller;
      // Auch hier gilt: nur der eigene Finger. Sonst braeche das Loslassen
      // des Laufjoysticks den Zielvorgang ab.
      const beendet = (event: PointerEvent) => {
        if (event.pointerId !== pointerId.current) return;
        pointerId.current = null;
        stickMode.current = false;
        setStick(null);
        skillInput.cancel();
        abbruch.current?.abort();
        abbruch.current = null;
      };
      window.addEventListener('pointerup', beendet, { signal: controller.signal });
      window.addEventListener('pointercancel', beendet, { signal: controller.signal });
    },
    [skillInput, snapshot.heroId, onCast],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (pointerId.current !== event.pointerId) return;
      skillInput.move(event.clientX, event.clientY);
      if (stickMode.current) {
        setStick({ ...toStick(stickOrigin.current, event.clientX, event.clientY) });
      }
    },
    [skillInput],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (pointerId.current !== event.pointerId) return;
      pointerId.current = null;
      abbruch.current?.abort();
      abbruch.current = null;

      const zielen = skillInput.end();
      const gestickt = stickMode.current;
      stickMode.current = false;
      setStick(null);
      if (!zielen) return;

      if (zielen.inDeadZone) {
        /*
         * Kaum bewegt - also kein Ziehen.
         *
         * Mit der Maus heisst das: Der Skill wandert in die Hand und wartet
         * auf den Zielklick. Mit dem Finger bleibt es beim bisherigen
         * Verhalten, weil ein Tipp am Handy schnell gehen soll und es dort
         * keinen Mauszeiger gibt, dem eine Vorschau folgen koennte.
         */
        /*
         * Kurzer Tipp oder Klick: Der Skill wandert in die Hand und wartet auf
         * den Zielklick. Am Handy gibt es keinen Mauszeiger, dem eine Vorschau
         * folgen koennte - dort zeigt sie deshalb in Blickrichtung, bis man
         * die Stelle antippt.
         */
        if (event.pointerType === 'mouse') {
          skillInput.arm(zielen.slot, event.clientX, event.clientY);
        } else {
          skillInput.arm(zielen.slot, null, null);
        }
        return;
      }

      if (gestickt) {
        onCast(zielen.slot, null, null, {
          deltaX: zielen.currentX - stickOrigin.current.x,
          deltaY: zielen.currentY - stickOrigin.current.y,
        });
        return;
      }
      onCast(zielen.slot, zielen.currentX, zielen.currentY);
    },
    [skillInput, onCast],
  );

  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (pointerId.current !== event.pointerId) return;
      pointerId.current = null;
      abbruch.current?.abort();
      abbruch.current = null;
      stickMode.current = false;
      setStick(null);
      skillInput.cancel();
    },
    [skillInput],
  );

  // Verschwindet die Leiste mitten im Zielen, bleibt sonst die Vorschau stehen.
  useEffect(() => {
    return () => {
      abbruch.current?.abort();
      abbruch.current = null;
      skillInput.cancel();
    };
  }, [skillInput]);

  const definitionen = getSkillsForHero(snapshot.heroId);
  if (definitionen.length === 0) return null;

  return (
    <>
      {/* Der Zielstick liegt ueber allem: Er zeigt, wie weit ausgelenkt ist. */}
      {stick && (
        <div
          className="dl-aimstick"
          style={{
            left: stick.originX,
            top: stick.originY,
            width: AIM_STICK_RADIUS * 2,
            height: AIM_STICK_RADIUS * 2,
          }}
        >
          <div
            className="dl-aimstick__knob"
            style={{ transform: `translate(${stick.knobX}px, ${stick.knobY}px)` }}
          />
        </div>
      )}

      <div className="dl-skillbar">
        {definitionen.map((definition) => (
          <SkillButton
            key={definition.id}
            definition={definition}
            snapshot={snapshot}
            aiming={aimingSlot === definition.slot}
            steuerung={steuerung}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          />
        ))}
      </div>
    </>
  );
}

/** Rechnet die Fingerposition in die Auslenkung des Zielsticks um. */
function toStick(
  origin: { x: number; y: number },
  pointerX: number,
  pointerY: number,
): { originX: number; originY: number; knobX: number; knobY: number } {
  const dx = pointerX - origin.x;
  const dy = pointerY - origin.y;
  const laenge = Math.hypot(dx, dy);
  const begrenzt = Math.min(laenge, AIM_STICK_RADIUS);

  return {
    originX: origin.x,
    originY: origin.y,
    knobX: laenge > 0 ? (dx / laenge) * begrenzt : 0,
    knobY: laenge > 0 ? (dy / laenge) * begrenzt : 0,
  };
}

interface ButtonProps {
  definition: SkillDefinition;
  snapshot: MatchSnapshot;
  aiming: boolean;
  /** Welche Taste hier steht, haengt an der gewaehlten Steuerung. */
  steuerung: Steuerung;
  onPointerDown: (event: React.PointerEvent<HTMLButtonElement>, slot: number) => void;
  onPointerMove: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLButtonElement>) => void;
}

function SkillButton({
  definition,
  snapshot,
  aiming,
  steuerung,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: ButtonProps) {
  const cooldown = snapshot.skillCooldowns[definition.slot] ?? 0;
  const bild = faehigkeitBild(definition.id);
  const abklingend = cooldown > 0;
  const zuWenigMana = snapshot.mana < definition.manaCost;
  // Das Ultimate gibt es erst ab Level 5 (Abschnitt 46).
  const nochNichtFrei = definition.slot === 4 && !isUltimateUnlocked(snapshot.level);
  const gesperrt = abklingend || zuWenigMana || !snapshot.heroAlive || nochNichtFrei;
  /*
   * Nur das Mana fehlt (16.10.2026): leichter ausgegraut als "gesperrt",
   * dazu "LOW MANA" im Kreis und rote Kosten. Laeuft die Abklingzeit oder
   * ist die Ultimate noch zu, sagt das schon der Knopf selbst.
   */
  const nurManaFehlt = zuWenigMana && !abklingend && !nochNichtFrei && snapshot.heroAlive;
  const ressource =
    getHero(snapshot.heroId)?.resource === 'energy' ? t.resource.energy : t.resource.mana;

  // Anteil der noch laufenden Abklingzeit - fuellt die Schaltflaeche von unten.
  const anteil = abklingend ? Math.min(1, cooldown / definition.cooldownSeconds) : 0;

  /*
   * Die Gruppe ist gebogen, nicht gerade (VISUAL_REFERENCE Abschnitt 20): Jeder
   * Slot bekommt seinen Platz im Daumenbogen. Die Groesse steigt zum Ultimate
   * hin an - Utility < Skill 1-3 < Ultimate.
   */
  const klassen = [
    'dl-skill',
    `dl-skill--slot${definition.slot}`,
    bild ? 'dl-skill--bild' : '',
    definition.slot === 4 ? 'dl-skill--ultimate' : '',
    gesperrt ? 'dl-skill--locked' : '',
    nurManaFehlt ? 'dl-skill--lowmana' : '',
    aiming ? 'dl-skill--aiming' : '',
  ]
    .filter(Boolean)
    .join(' ');

  /* Die Abklingzeit wird als Fuellung gezeigt UND als Zahl - eine reine
     Farbflaeche waere im Gefecht zu ungenau abzulesen. */
  const abklingFuellung = (
    <span className="dl-skill__cooldown" style={{ height: `${anteil * 100}%` }} />
  );

  return (
    <button
      type="button"
      className={klassen}
      title={
        nochNichtFrei
          ? `${definition.name} - ${t.hero.ultimateLocked}`
          : `${definition.name} - ${definition.description}`
      }
      onPointerDown={(event) => onPointerDown(event, definition.slot)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {/*
        Das Symbol der Faehigkeit (16.10.2026) fuellt den Kreis; Abklingzeit,
        Taste und Kosten liegen darueber. Gibt es noch keines, bleibt der
        schlichte Kreis.

        Bild und Abklingfuellung liegen zusammen in einer runden Huelle, die
        sie abschneidet - der Knopf selbst schneidet dann nichts mehr ab, und
        das Tasten-Schild darf ueber seine Kante ragen (16.10.2026).
      */}
      {bild ? (
        <span className="dl-skill__rund" aria-hidden="true">
          <picture className="dl-skill__bild">
            <source type="image/avif" srcSet={bild.avif} sizes="96px" />
            <source type="image/webp" srcSet={bild.webp} sizes="96px" />
            <img src={bild.ersatz} alt="" decoding="async" />
          </picture>
          {abklingFuellung}
        </span>
      ) : (
        abklingFuellung
      )}
      {/*
        Im Kreis steht nur das Noetigste: Taste und Kosten. Der Name wuerde den
        Knopf sprengen - er steht im Tooltip und spaeter im Bild des Skills
        (VISUAL_REFERENCE Abschnitt 55).
      */}
      <span className="dl-skill__key">{skillBeschriftung(steuerung, definition.slot)}</span>
      <span className="dl-skill__cost">{definition.manaCost}</span>
      {abklingend && <span className="dl-skill__timer">{Math.ceil(cooldown)}</span>}
      {nurManaFehlt && (
        <span className="dl-skill__hint">
          <span>{t.skills.low}</span>
          <span>{ressource}</span>
        </span>
      )}
    </button>
  );
}
